import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { arrayProblems } from '../data/problems/arrays';
import { stringProblems } from '../data/problems/strings';
import { linkedListProblems } from '../data/problems/linked-lists';
import { stackQueueProblems } from '../data/problems/stack-queue';
import { treeProblems } from '../data/problems/trees';
import { PracticeProblem } from '../components/PracticeProblem';

interface Problem {
  id: string;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  description: string;
  examples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  starterCode: Record<string, string>;
  testCases: Array<{
    input: string;
    output: string;
  }>;
}

const Practice = () => {
  const { topic } = useParams<{ topic: string }>();
  const navigate = useNavigate();
  const [selectedProblem, setSelectedProblem] = useState<string | null>(null);

  const handleQuizClick = () => {
    navigate('/quiz/data-structures');
  };

  const getProblems = () => {
    switch (topic) {
      case 'arrays':
        return arrayProblems;
      case 'strings':
        return stringProblems;
      case 'linked-lists':
        return linkedListProblems;
      case 'stack-queue':
        return stackQueueProblems;
      case 'trees':
        return treeProblems;
      default:
        return [];
    }
  };

  const problems = getProblems();

  // Group problems by difficulty
  const groupedProblems = {
    Easy: problems.filter(p => p.difficulty === 'Easy'),
    Medium: problems.filter(p => p.difficulty === 'Medium'),
    Hard: problems.filter(p => p.difficulty === 'Hard')
  };

  const formattedTopic = topic?.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  return (
    <div className="min-h-screen bg-black text-white/90">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-normal tracking-wide text-white mb-8"
        >
          {formattedTopic} Practice
        </motion.h1>

        <div className="flex justify-end items-center mb-8">
          {topic !== 'strings' && topic !== 'recursion' && topic !== 'control-flow' && (
            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleQuizClick}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center border border-white/10"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Take Quiz
            </motion.button>
          )}
        </div>

        {selectedProblem ? (
          <div>
            <PracticeProblem 
              problem={problems.find(p => p.id === selectedProblem)!}
              onBack={() => setSelectedProblem(null)}
            />
          </div>
        ) : (
          <div>
            {Object.entries(groupedProblems).map(([difficulty, problems]) => (
              problems.length > 0 && (
                <motion.div 
                  key={difficulty} 
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <h2 className="text-2xl font-normal tracking-wide text-white mb-4">{difficulty}</h2>
                  <div className="grid gap-4">
                    {problems.map((problem) => (
                      <motion.div
                        key={problem.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -5 }}
                        className="bg-white/[0.02] p-6 rounded-lg cursor-pointer border border-white/10 hover:border-white/20 transition-all"
                        onClick={() => setSelectedProblem(problem.id)}
                      >
                        <h3 className="text-lg font-normal text-white">{problem.title}</h3>
                        <div className="mt-2 text-sm text-white/60">
                          Difficulty: <span className="font-medium text-white/80">{difficulty}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Practice;
